<template>
  <div class="medical-info-wrapper">
    <section-title has-line title="Información médica del estudiante" />
    <div class="row mx-0 mt-2">
      <div class="col-12 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-text-area
            v-model="value.healthCondition"
            label="Condiciones de salud que deben ser tomadas en consideración al momento de la evaluación: (Ej: osteogénesis imperfecta, hipoglucemia, espina bífida, úlceras, asma, arritmia cardiaca, entre otros)"
            :maxlength="3000"
            show-char-count
            :count-classes="['d-block text-right']"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor explique las condiciones de salud que deben ser tomadas en consideración."
          />
        </validation-provider>
      </div>
      <div class="col-md-6 col-12 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-radio
            v-model="value.surgicalIntervention"
            class="extended_radio"
            name="medicalIntervention"
            label="¿Ha sido intervenido quirúrgicamente o lo será próximamente?"
            :options="yesNoOptions"
            is-inline
            :error="Boolean(errors[0])"
            :disabled="true"
            custom-error-msg="Favor marque si o no."
          />
        </validation-provider>
      </div>
      <div v-if="value.surgicalIntervention" class="col-md-6 col-12 mb-2">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-date-picker
            v-model="value.surgicalInterventionDate"
            label="Especifique la fecha:"
            placeholder="DD/MM/YYYY"
            :max-date="new Date()"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor seleccione fecha."
          />
        </validation-provider>
      </div>
      <div class="col-12 mb-3">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-radio
            v-model="value.medicationSchoolHours"
            class="extended_radio"
            name="schedule"
            label="¿El estudiante se medica en horario escolar?"
            :options="yesNoOptions"
            is-inline
            :disabled="readonly"
            :error="Boolean(errors[0])"
            custom-error-msg="Favor marque si o no."
          />
        </validation-provider>
      </div>
      <div v-if="value.medicationSchoolHours" class="col-12 mb-2">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-text-area
            v-model="value.explainMedication"
            label="Especifique:"
            :maxlength="3000"
            show-char-count
            :count-classes="['d-block text-right']"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor indique las medicaciones en horario escolar."
          />
        </validation-provider>
      </div>
      <div class="col-12 mb-2">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-text-area
            v-model="value.allergy"
            label="Es alérgico a:"
            :maxlength="3000"
            show-char-count
            :disabled="readonly"
            :count-classes="['d-block text-right']"
            :error="errors[0]"
            custom-error-msg="Favor indique si es alérgico."
          />
        </validation-provider>
      </div>
      <div class="col-12 px-0 mb-5">
        <validation-observer ref="equipmentSelection" disabled>
          <div class="row mx-0">
            <div class="col-12 col-md-4">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="equipment.selected"
                  label="Equipos de ayuda que utiliza el estudiante:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  :disabled="readonly"
                  custom-error-msg="El equipo es requerido."
                  :options="availableEquipments"
                />
              </validation-provider>
            </div>
            <div v-if="isOtherSelected" class="col-12 col-md-4">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="equipment.otherDescription"
                  label="Descripción:"
                  :error="errors[0]"
                  :disabled="readonly"
                  custom-error-msg="La descripción de otros es requerida"
                />
              </validation-provider>
            </div>
            <div class="col-12 col-md-2 pt-2">
              <action-button
                v-if="!readonly"
                class="mt-md-4"
                variant="success"
                i="fas fa-file-plus"
                text="Añadir"
                @click="addEquipment"
              />
            </div>
            <div class="col-12 mt-2">
              <span
                v-for="(item, idx) of value.equipments"
                :key="`pill-${idx}`"
                class="equipment-pill"
              >
                {{ item.name }}
                <em
                  v-show="!readonly"
                  class="far fa-times-circle color-red ml-1"
                  @click="removeEquipment(idx)"
                />
              </span>
            </div>
          </div>
        </validation-observer>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomTextArea from 'CustomTextArea';
import CustomDatePicker from 'custom-date-picker';
import CustomRadio from 'custom-radio';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import { getStudentEquipments } from '@/modules/common/code.api.js';

const OtherEquipment = 1;

export default {
  name: 'StudentMedicalInfo',
  components: {
    SectionTitle,
    CustomTextArea,
    CustomDatePicker,
    CustomRadio,
    CustomDropDown,
    ActionButton,
    CustomInput,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    yesNoOptions: [
      {
        value: true,
        label: 'SI',
      },
      {
        value: false,
        label: 'NO',
      },
    ],
    equipment: {
      selected: null,
      otherDescription: null,
    },
    equipments: [],
    disabled: false,
  }),
  computed: {
    isOtherSelected() {
      return this.equipment.selected?.id == OtherEquipment;
    },
    availableEquipments() {
      return this.equipments.filter((d) => {
        return this.value.equipments.every((item) => {
          if (item.id != OtherEquipment) {
            return item.id != d.id;
          }
          return true;
        });
      });
    },
  },
  async created() {
    await this.getEquipments();
  },
  methods: {
    async getEquipments() {
      const { data } = await getStudentEquipments();
      this.equipments = [...data];
    },
    async addEquipment() {
      const isValid = await this.$refs.equipmentSelection.validate();
      if (!isValid) return;
      const equipment = {
        id: this.equipment.selected.id,
        name: this.isOtherSelected
          ? `${this.equipment.selected.name}-${this.equipment.otherDescription}`
          : this.equipment.selected.name,
        otherDescription: this.isOtherSelected
          ? this.equipment.otherDescription
          : '',
      };
      this.value.equipments.push(equipment);
      this.equipment = {
        selected: null,
        otherDescription: null,
      };
      this.$refs.equipmentSelection.reset();
    },
    removeEquipment(idx) {
      const equipment = this.value.equipments[idx];
      if (equipment.id == OtherEquipment) this.value.equipmentOther = null;
      this.value.equipments.splice(idx, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.medical-info-wrapper {
  .equipment-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.5rem;
    border: 1px solid #b5c3bc;
    border-radius: 50rem;
    margin: 0.25rem 0.5rem;
    .far {
      cursor: pointer;
    }
  }

  ::v-deep .extended_radio {
    .col-12 {
      padding-left: 0;
    }
  }
  .tag-badge {
    display: inline-flex;
    align-items: center;
    padding-right: 1rem;
    height: 42px;
    background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0%
      0% no-repeat padding-box;
    border-radius: 50rem;

    .orange-icon-badge-align {
      vertical-align: top;
    }
    .tag-badge-title {
      display: inline-block;
      text-align: left;
      font-size: 2rem;
      font-weight: bold;
      color: #11291b;
    }
    .dot {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 35px;
      height: 35px;
      border-radius: 50%;
      font-size: 1.8rem;
      background: #ffffff 0% 0% no-repeat padding-box;
      box-shadow: 0px 3px 6px #00000029;
      text-align: center;
    }
  }
}
</style>
